/**
 * ActiveSportProvider — single source of truth for Global Sport Mode (ADR-030).
 * Distinct from session SportProvider (ADR-032), which resolves live Match engines.
 *
 * Overlay open state lives here so Me → Change Sport and cold-start prompts
 * share one experience without route navigation.
 */

import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from "react";
import {
  getActiveSportId,
  hasChosenActiveSport,
  setActiveSportId as persistActiveSportId,
  shouldPromptActiveSportSelection,
} from "../domain/sport/activeSport";
import {
  acknowledgeSportSelectionThisSession,
  hasAcknowledgedSportSelectionThisSession,
} from "../domain/sport/sportSelectionFrequency";
import { getSportEntrySplashPhrase } from "../domain/sport/buildSportSelectionCards";
import { DEFAULT_SPORT, normalizeSportId } from "../domain/sport/sportTypes";
import { getSportSelectionPresentation } from "../domain/sport/getSportSelectionPresentation";
import { listPlayableSportModules, resolveSportModule } from "./registry";

const ActiveSportContext = createContext(null);

const SPLASH_MS = 420;

export function ActiveSportProvider({ children }) {
  const [sportId, setSportIdState] = useState(() => getActiveSportId());
  const [hasChosen, setHasChosen] = useState(() => hasChosenActiveSport());
  const [revision, setRevision] = useState(0);
  const [selectionOpen, setSelectionOpen] = useState(false);
  const [selectionMode, setSelectionMode] = useState("launch");
  const [entrySplash, setEntrySplash] = useState(null);

  const syncNeedsSelection = useCallback(() => {
    if (shouldPromptActiveSportSelection()) {
      setSelectionMode("launch");
      setSelectionOpen(true);
    }
  }, []);

  useEffect(() => {
    syncNeedsSelection();
  }, [syncNeedsSelection, revision]);

  const setActiveSport = useCallback((nextId) => {
    const id = persistActiveSportId(nextId);
    setSportIdState(id);
    setHasChosen(true);
    setRevision((n) => n + 1);
    return id;
  }, []);

  const openSportSelection = useCallback((mode = "change") => {
    setSelectionMode(mode === "launch" ? "launch" : "change");
    setSelectionOpen(true);
    setEntrySplash(null);
  }, []);

  const closeSportSelection = useCallback(() => {
    setSelectionOpen(false);
    setEntrySplash(null);
  }, []);

  const confirmSport = useCallback(
    (nextId) => {
      acknowledgeSportSelectionThisSession();
      const id = setActiveSport(nextId);
      const presentation = getSportSelectionPresentation(id);
      setEntrySplash({
        sportId: id,
        displayName: presentation.displayName,
        emoji: presentation.emoji,
        glyph: presentation.glyph,
        phrase: getSportEntrySplashPhrase(id),
      });
      window.setTimeout(() => {
        setEntrySplash(null);
        setSelectionOpen(false);
      }, SPLASH_MS);
      return id;
    },
    [setActiveSport]
  );

  const refreshFromStorage = useCallback(() => {
    setSportIdState(getActiveSportId());
    setHasChosen(hasChosenActiveSport());
    setRevision((n) => n + 1);
  }, []);

  const value = useMemo(() => {
    const id = normalizeSportId(sportId || DEFAULT_SPORT);
    const module = resolveSportModule(id);
    const presentation = getSportSelectionPresentation(id);
    const playable = listPlayableSportModules();
    const needsSelection = shouldPromptActiveSportSelection();
    return {
      sportId: id,
      module,
      presentation,
      hasChosen,
      needsSelection,
      selectionOpen: selectionOpen || needsSelection,
      selectionMode,
      entrySplash,
      playableSports: playable,
      setActiveSport,
      confirmSport,
      openSportSelection,
      closeSportSelection,
      refreshFromStorage,
      sessionAcked: hasAcknowledgedSportSelectionThisSession(),
      revision,
    };
  }, [
    sportId,
    hasChosen,
    selectionOpen,
    selectionMode,
    entrySplash,
    setActiveSport,
    confirmSport,
    openSportSelection,
    closeSportSelection,
    refreshFromStorage,
    revision,
  ]);

  return (
    <ActiveSportContext.Provider value={value}>{children}</ActiveSportContext.Provider>
  );
}

/**
 * Global active sport for platform surfaces. Prefer this over sportId props.
 */
export function useActiveSport() {
  const ctx = useContext(ActiveSportContext);
  if (!ctx) {
    const id = getActiveSportId();
    return {
      sportId: id,
      module: resolveSportModule(id),
      presentation: getSportSelectionPresentation(id),
      hasChosen: hasChosenActiveSport(),
      needsSelection: shouldPromptActiveSportSelection(),
      selectionOpen: false,
      selectionMode: "launch",
      entrySplash: null,
      playableSports: listPlayableSportModules(),
      setActiveSport: persistActiveSportId,
      confirmSport: persistActiveSportId,
      openSportSelection: () => {},
      closeSportSelection: () => {},
      refreshFromStorage: () => {},
      sessionAcked: hasAcknowledgedSportSelectionThisSession(),
      revision: 0,
    };
  }
  return ctx;
}

export { ActiveSportContext };
